"use client";

import { Citrus, FlaskConical, Barrel, Wine, Truck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const steps = [{
  number: "01",
  title: "SPECIFY",
  Icon: Citrus,
  description: "Submit your part list, CAD files, or technical specifications. We review every requirement for material compatibility and compliance standards."
}, {
  number: "02",
  title: "PROGRAM",
  Icon: FlaskConical,
  description: "Our technicians translate your data into precise laser paths. We calibrate depth, speed, and power settings for your specific substrate."
}, {
  number: "03",
  title: "ENGRAVE",
  Icon: Barrel,
  description: "The laser marks your components with permanent, high-contrast detail. We maintain strict tolerances on every piece, from tags to panels."
}, {
  number: "04",
  title: "VERIFY",
  Icon: Wine,
  description: "Every order undergoes a final quality inspection. We document the results, package the parts securely, and ship them to your facility."
}, {
  number: "05",
  title: "DELIVER",
  Icon: Truck,
  description: "Your custom engraved components are dispatched via priority logistics, ensuring they arrive on-site ready for immediate installation."
}];
export default function Engravingorderprocess() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3883" id="engravingorderprocess" className="bg-[#FFFFFF] text-[#0F1B2D] relative overflow-hidden py-32 md:py-[200px]">
      <div className="pointer-events-none absolute top-0 left-4 hidden h-full lg:left-8 lg:block">
        <div className="sticky top-32">
          <div className="font-default text-[10px] font-medium tracking-[0.3em] uppercase text-[#0F1B2D]/50" style={{
          writingMode: "vertical-rl"
        }}>
            00 &mdash; THE WORKFLOW
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-20">
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:col-span-7">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-[#C9A227]" />
              <span className="font-default text-xs font-medium tracking-[0.25em] uppercase text-[#C9A227]">
                Production Protocol
              </span>
            </div>
            <h2 className="font-default text-4xl font-bold leading-[1.1] tracking-tight text-[#0F1B2D] sm:text-5xl md:text-6xl lg:text-[64px]">
              How Do You Order
              <br />
              Custom Phenolic Engraving?
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-6">
            <p className="font-default text-lg leading-[1.7] text-[#0F1B2D] md:text-xl">
              At Evermark, we treat every order as a critical component. Whether you need a single custom legend plate or a thousand asset tags, our process ensures permanent, compliant identification.
            </p>
            <p className="mt-5 font-default text-base leading-[1.7] text-[#0F1B2D]/80">
              No guesswork. No errors. Just the exact marking your project requires, delivered on time and to spec.
            </p>
          </div>
        </div>

        <div className="relative mt-20 md:mt-28">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-5 lg:gap-4">
            {steps.map((step, i) => {
            const card = useRevealOnScroll<HTMLDivElement>({
              threshold: 0.15
            });
            const Icon = step.Icon;
            return <div key={step.number} data-index={i} ref={card.ref} className={`group relative transition-all duration-700 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {i < steps.length - 1 && <div className="pointer-events-none absolute top-1/2 -right-4 z-20 hidden lg:block">
                      <div className="flex items-center gap-0.5">
                        <span className="h-px w-2 bg-[#C9A227]" />
                        <span className="h-px w-1.5 bg-[#C9A227]/60" />
                        <span className="h-px w-1 bg-[#C9A227]/30" />
                      </div>
                    </div>}

                  <div className="relative h-[420px] overflow-hidden border border-[#0F1B2D]/10 bg-white shadow-md transition-all duration-500 group-hover:-translate-y-1 group-hover:border-[#C9A227] group-hover:shadow-xl rounded-lg">
                    <div className="absolute -top-2 -left-2 select-none">
                      <span className="font-default text-[48px] font-extrabold leading-none tracking-tighter text-[#C9A227] transition-colors duration-[400ms]" aria-hidden="true" style={{
                    fontSize: "2.5rem"
                  }}>
                        {step.number}
                      </span>
                    </div>

                    <div className="relative flex h-full flex-col justify-between p-8 pt-24">
                      <Icon className="h-10 w-10 text-[#0F1B2D]/80 transition-colors duration-300 group-hover:text-[#C9A227]" strokeWidth={1.5} />

                      <div>
                        <div className="mb-4 h-px w-8 bg-[#0F1B2D]/20 transition-all duration-500 group-hover:w-16 group-hover:bg-[#C9A227]" />
                        <h3 className="font-default text-xl font-bold tracking-[0.1em] uppercase text-[#0F1B2D]">
                          {step.title}
                        </h3>
                        <p className="mt-4 font-default text-[15px] leading-[1.6] text-[#0F1B2D]/80">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#C9A227] to-transparent" />
                    </div>
                  </div>
                </div>;
          })}
          </div>

          <div className="mt-20 flex justify-center">
            <Button asChild className="bg-[#C9A227] text-[#0F1B2D] hover:bg-[#C9A227]/90 font-bold px-10 py-6 text-lg shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-lg">
              <Link to="/ContactPage">
                Start Your Order Process
              </Link>
            </Button>
          </div>

          <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-[#0F1B2D]/10 pt-8 sm:flex-row sm:items-center">
            <p className="font-default text-xs tracking-[0.25em] uppercase text-[#0F1B2D]/50">
              Five steps &mdash; one standard
            </p>
            <p className="font-default italic text-sm text-[#0F1B2D]/60">
              Permanent. Compliant. Specified by name.
            </p>
          </div>
        </div>
      </div>
    </section>;
}